import React, { useRef, useState, ChangeEvent } from 'react';
import { usePostApiMasterScheduleUploadMutation, useGetApiProjectsQuery } from '../../../../app/athenaApiGen';
import { FormControl, InputLabel, Select, MenuItem, FormHelperText, SelectChangeEvent, TextField } from '@mui/material';

interface UploaderProps {
  onUpload?: (file: File) => Promise<void> | void;
  onUploadStart?: () => void;
}

const Uploader: React.FC<UploaderProps> = ({ onUpload, onUploadStart }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [projectNameOverride, setProjectNameOverride] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [error, setError] = useState('');
  const [upload] = usePostApiMasterScheduleUploadMutation();
  const { data: projectsData, isLoading: projectsLoading } = useGetApiProjectsQuery({
    pageSize: 100,
    includeArchived: false,
  });

  const projects = (projectsData?.projects || []).filter((p) => p.workflowState === "Internally Approved");

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload.');
      return;
    }

    setUploading(true);
    setError('');
    setUploadStatus(`Uploading ${selectedFile.name}...`);

    // Notify parent that upload started
    if (onUploadStart) {
      onUploadStart();
    }

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      if (selectedProjectId) {
        const selectedProject = projects.find(p => p.projectId === selectedProjectId);
        formData.append('project_id', selectedProjectId);

        // Use project name override if provided, otherwise use selected project's title
        const projectName = projectNameOverride.trim() || selectedProject?.title;
        if (projectName) {
          formData.append('project_name', projectName);
        }
      } else if (projectNameOverride.trim()) {
        // Program-level IMS with optional name override
        formData.append('project_name', projectNameOverride.trim());
      }

      await upload({ body: formData as any }).unwrap();

      // Upload completed successfully
      setUploadStatus(`Upload complete: ${selectedFile.name}`);
      setSelectedFile(null);
      setSelectedProjectId('');
      setProjectNameOverride('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Notify parent of successful upload (triggers context refresh and modal close)
      if (onUpload) {
        await onUpload(selectedFile);
      }
    } catch (err: any) {
      const errorMessage = err?.data?.error || err?.message || 'Unknown error';
      setError('Upload failed: ' + errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    setError('');
  };

  const handleProjectChange = (e: SelectChangeEvent<string>) => {
    setSelectedProjectId(e.target.value);
    setError('');
  };

  return (
    <div>
      <FormControl fullWidth sx={{ mb: 2 }} disabled={uploading || projectsLoading}>
        <InputLabel id="project-select-label">Project (Optional)</InputLabel>
        <Select
          labelId="project-select-label"
          id="project-select"
          value={selectedProjectId}
          label="Project (Optional)"
          onChange={handleProjectChange}
        >
          <MenuItem value="">
            <em>None - Program Level IMS</em>
          </MenuItem>
          {projects.map((project) => (
            <MenuItem key={project.projectId} value={project.projectId}>
              {project.projectCode ? `${project.title} (${project.projectCode})` : project.title}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>Select a project, or leave empty for program-level IMS</FormHelperText>
      </FormControl>

      <TextField
        fullWidth
        label="Project Name Override (Optional)"
        value={projectNameOverride}
        onChange={(e) => setProjectNameOverride(e.target.value)}
        disabled={uploading}
        helperText="Leave blank to use the selected project's name"
        sx={{ mb: 2 }}
      />

      <input
        accept=".mpp"
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        disabled={uploading}
      />
      <button
        onClick={handleUpload}
        disabled={uploading || !selectedFile}
        style={{
          marginTop: '10px',
          padding: '10px 20px',
          backgroundColor: uploading || !selectedFile ? '#ccc' : '#1976d2',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: uploading || !selectedFile  ? 'not-allowed' : 'pointer',
        }}
      >
        {uploading ? 'Uploading...' : 'Upload'}
      </button>
      {error && <div style={{ color: 'red', marginTop: '10px' }}>{error}</div>}
      {uploadStatus && <p>{uploadStatus}</p>}
    </div>
  );
};

export default Uploader;
